import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';

import { FullDiskAccessBanner } from './app-setup-configuration';
import { useNotesFoldersQuery } from './apple-notes-folders';

import type { ConfigurationContext } from './configuration.types';

const LIST_FRAME = 'rounded-lg border border-border bg-surface';

function NotesFolders({ serverSlug }: { serverSlug: string }) {
  const query = useNotesFoldersQuery(serverSlug);

  if (query.isPending) {
    return (
      <XStack center className={`${LIST_FRAME} gap-2 px-4 py-4`}>
        <Icons.loader className="size-3.5 animate-spin text-foreground-muted" />
        <span className="text-xs text-foreground-muted">
          Loading folders from Notes…
        </span>
      </XStack>
    );
  }

  if (query.isError) {
    return (
      <YStack
        className={`${LIST_FRAME} items-center gap-3 px-4 py-6 text-center`}
      >
        <p className="text-xs text-foreground-muted">
          Couldn’t load folders from Notes.
        </p>
        <Button
          variant="secondary"
          size="sm"
          isLoading={query.isFetching}
          onClick={() => void query.refetch()}
        >
          Try again
        </Button>
      </YStack>
    );
  }

  const folders = query.data;
  return (
    <YStack className="gap-2">
      <XStack className="items-baseline gap-1.5">
        <span className="text-xs font-medium text-foreground-muted">
          Folders
        </span>
        <span className="text-xs text-foreground-subtle">{folders.length}</span>
      </XStack>
      {folders.length === 0 ? (
        <p className={`${LIST_FRAME} px-4 py-3 text-xs text-foreground-muted`}>
          No folders found in Notes.
        </p>
      ) : (
        <ul className={`${LIST_FRAME} divide-y divide-border overflow-hidden`}>
          {folders.map((folder) => (
            <li
              key={`${folder.accountName}/${folder.name}`}
              className="flex items-center gap-3 px-4 py-2.5"
            >
              <Icons.folder className="size-4 shrink-0 text-foreground-muted" />
              <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                {folder.name}
              </span>
              <span className="shrink-0 text-xs text-foreground-muted">
                {folder.noteCount} {folder.noteCount === 1 ? 'note' : 'notes'}
              </span>
              <span className="w-32 shrink-0 truncate text-right text-xs text-foreground-subtle">
                {folder.accountName}
              </span>
            </li>
          ))}
        </ul>
      )}
    </YStack>
  );
}

export function AppleNotesConfiguration({
  context,
}: {
  context: ConfigurationContext;
}) {
  const source = context.localSource;
  if (source?.kind !== 'local_mcp') return null;

  return (
    <YStack className="gap-4 p-6">
      <FullDiskAccessBanner />
      <NotesFolders serverSlug={source.serverSlug} />
    </YStack>
  );
}
